import { ResourceListByType, SkillTargetAreaTextureGeneration } from '../entities/resources';
import { SwatchedPartTextureGeneration } from '../game/swatch.types';
import { KeyedNumberList } from '../general';
import { ICoord2D } from '../geometry';
import { TerraformingClientBootstrap } from '../match-types/terraforming/terraforming-match';
import { EncodedSchemaObj } from '../schema/encoded-schema';

import { NavMeshWireSnapshot } from './nav-mesh-wire';

/** Coarse per-entity init-pipe container; deep typing is a separate epic. */
export type MatchInitEntityCont = Record<string, unknown>;

/** Debug collider body from AGE map transport. */
export interface MatchInitMapCollider {
  vertices: ICoord2D[];
}

/**
 * Shared one-shot match-init envelope from AGE `getDataForInitialTransport`.
 * Mode-specific extras live only on {@link MatchInitTransportOr} / {@link MatchInitTransportTf}.
 */
export interface MatchInitTransportBase {
  id: string;
  pxPerGameUnit: number;
  mapWidthPx: number;
  mapHeightPx: number;
  knownResourceData: ResourceListByType;
  teamShadedTextureGeneration: KeyedNumberList;
  dirtyPartTextureGeneration: KeyedNumberList;
  swatchedPartTextureGeneration: SwatchedPartTextureGeneration[];
  targetAreaTileTextureGeneration: SkillTargetAreaTextureGeneration;
  entities: MatchInitEntityCont;
  encodedMatchSchemaData: EncodedSchemaObj;
  /** Debug-only; may be absent or empty outside debug. */
  mapColliders?: MatchInitMapCollider[];
  /**
   * Active pathing mesh (boundary + holes + triangles) for overlays / single-mesh clients.
   */
  pathingMesh?: NavMeshWireSnapshot;
}

export type MatchInitTransportOr = MatchInitTransportBase & {
  roomSpawnPoints: Record<string, ICoord2D>;
};

export type MatchInitTransportTf = MatchInitTransportBase & {
  terraformBootstrap: TerraformingClientBootstrap;
};

/** True when the transport includes Outpost Run room spawn points. */
export function isMatchInitTransportOr(data: unknown): data is MatchInitTransportOr {
  return typeof data === 'object'
    && data !== null
    && 'roomSpawnPoints' in data
    && data.roomSpawnPoints !== undefined
    && data.roomSpawnPoints !== null;
}

/** True when the transport includes Terraforming client bootstrap. */
export function isMatchInitTransportTf(data: unknown): data is MatchInitTransportTf {
  return typeof data === 'object'
    && data !== null
    && 'terraformBootstrap' in data
    && data.terraformBootstrap !== undefined
    && data.terraformBootstrap !== null;
}

/**
 * Mutable container AGE managers fill during assembly.
 * All required {@link MatchInitTransportBase} keys are set before Match returns.
 */
export type MatchInitTransportCont = {
  [K in keyof MatchInitTransportBase]?: MatchInitTransportBase[K];
};

/** Resource / texture fields peeled into the preload scene. */
export interface MatchPreloadInitFields {
  knownResourceData: ResourceListByType;
  teamShadedTextureGeneration: KeyedNumberList;
  swatchedPartTextureGeneration: SwatchedPartTextureGeneration[];
  dirtyPartTextureGeneration: KeyedNumberList;
  targetAreaTileTextureGeneration: SkillTargetAreaTextureGeneration;
}

/** Active-scene fields shared by base / ZR (and as the OR/TF active core). */
export interface MatchActiveInitFieldsBase {
  matchId: string;
  mapWidthPx: number;
  mapHeightPx: number;
  entities: MatchInitEntityCont;
  encodedMatchSchemaData: EncodedSchemaObj;
}
